<template>
    <widget-card>
        <template #header>
            <QrCode class="widget-icon" />
            <span class="widget-card-header__title">
                {{ trans('dashboard.v2.testing_compliance.by_asset_type.title') }}
            </span>
        </template>
        <template #header-end>
            <div class="flex items-center gap-4 text-[0.8125rem] text-body">
                <span v-for="status in legendStatuses" :key="status" class="inline-flex items-center gap-1.5 whitespace-nowrap">
                    <TestingComplianceStatusIcon :status="status" :size="18" />
                    <span>{{ trans(`dashboard.v2.testing_compliance.status.${status}`) }}</span>
                </span>
            </div>
        </template>

        <div v-if="loading" class="compliance-donut-row" style="--donut-cols: 6">
            <SkeletonLoader v-for="i in 6" :key="i">
                <div class="h-[170px] rounded-lg"></div>
            </SkeletonLoader>
        </div>

        <empty-widget-state v-else-if="!hasAnyAssets" :text="trans('dashboard.v2.testing_compliance.by_asset_type.empty_state')" />

        <div v-else class="flex flex-col gap-4">
            <div v-for="group in groups" :key="group.airport_id ?? 'merged'">
                <div v-if="groups.length > 1" class="compliance-donut-airport">{{ group.airport_name }}</div>
                <div class="compliance-donut-row" :style="{ '--donut-cols': group.types.length || 6 }">
                    <TestingComplianceDonutCard
                        v-for="item in group.types"
                        :key="item.type"
                        :label="item.label"
                        :untested="item.untested"
                        :pending="item.pending"
                        :approved="item.approved"
                        :inoperable="item.inoperable ?? 0"
                        :compliant-percentage="item.compliant_percentage"
                    />
                </div>
            </div>
        </div>
    </widget-card>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import WidgetCard from '@/Pages/DashboardV2/Components/WidgetCard.vue';
import EmptyWidgetState from '@/Pages/DashboardV2/Components/EmptyWidgetState.vue';
import TestingComplianceDonutCard from '@/Pages/DashboardV2/Components/TestingCompliance/TestingComplianceDonutCard.vue';
import TestingComplianceStatusIcon from '@/Pages/DashboardV2/Components/TestingCompliance/TestingComplianceStatusIcon.vue';
import SkeletonLoader from '@/Components/SkeletonLoader.vue';
import QrCode from '@/../icons/QrCode.vue';
import { eventBus, events } from '@/eventBus';
import { $http } from '@/bootstrap';
import { trans } from '@/mixins/i18n';
import { buildUrlWithQuery } from '@/Utils/buildUrlWithQuery';
import { useComplianceShift } from '@/composables/useComplianceShift';

const props = defineProps({
    widgetData: {
        type: Object,
        required: true,
        default: () => ({}),
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
});

const dataUrl = computed(() => props.widgetData.dataUrl);

const legendStatuses = ['inoperable', 'approved', 'pending', 'untested'];

const loading = ref(true);
const groups = ref([]);

// The global shift filter lives in the page header (DetailsPage); its value
// rides in the shared `filters`. This card publishes the switcher options
// (`shiftMeta`) from its response and re-fetches the donuts bucketed by the
// selected shift.
const { shiftMeta } = useComplianceShift();

// The backend always returns one entry per donut type (even with zero
// counts), so list-length isn't a useful empty signal. Only show the empty
// state when the airports in view have no relevant assets across any type.
const hasAnyAssets = computed(() =>
    groups.value
        .flatMap((group) => group.types ?? [])
        .some((t) => (t.untested ?? 0) + (t.pending ?? 0) + (t.approved ?? 0) + (t.inoperable ?? 0) + (t.unscheduled ?? 0) > 0),
);

const fetchData = async (showLoading = false) => {
    try {
        loading.value = showLoading;
        const formData = props.filters?.form?.data?.() ?? {};
        const url = buildUrlWithQuery(dataUrl.value, { locations: formData.locations, shift: formData.shift ?? undefined });
        const response = await $http.get(url);
        groups.value = response.data.groups ?? [];
        shiftMeta.value = response.data.shifts ?? null;
    } catch (error) {
        groups.value = [];
        shiftMeta.value = null;
        console.error('Failed to fetch testing compliance by asset type:', error);
    } finally {
        loading.value = false;
    }
};

// Re-fetch (donuts re-bucket) whenever a filter changes, including the shift.
watch(
    () => props.filters,
    () => fetchData(),
    { deep: true },
);

onMounted(() => {
    fetchData(true);
    eventBus.$on(events.dashboardWidgetRefresh, fetchData);
});

onBeforeUnmount(() => {
    eventBus.$off(events.dashboardWidgetRefresh, fetchData);
    // `shiftMeta` is module-scoped (shared with the page header); clear it on
    // unmount so a later SPA visit to another scope doesn't default the shift
    // filter from a stale previous airport before this widget refetches.
    shiftMeta.value = null;
});
</script>

<style scoped>
:deep(.widget-card-body) {
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
}

.compliance-donut-airport {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-body);
    margin-bottom: 0.5rem;
}

.compliance-donut-row {
    display: grid;
    grid-template-columns: repeat(var(--donut-cols, 6), minmax(0, 1fr));
    row-gap: 1.5rem;
    column-gap: 1rem;
    width: 100%;
}

@media (max-width: 991px) {
    .compliance-donut-row {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (max-width: 575px) {
    .compliance-donut-row {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
